import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSelector from '../../redux/typedHooks';

const setInjectedPuzzelChatVisibility = (isVisible: boolean) => {
  document.querySelectorAll<HTMLElement>('[data-puzzel-chat]').forEach((chatElement) => {
    // eslint-disable-next-line no-param-reassign
    chatElement.style.display = isVisible ? '' : 'none';
  });
};

export const usePuzzelChat = (euwa?: EUWA, isEnabled = true) => {
  const [chat, setChat] = useState<ApplicationBridge | undefined>();
  const [shouldGetChatVariables, setShouldGetChatVariables] = useState(false);
  const chatRef = useRef<ApplicationBridge | undefined>(undefined);

  const customerInformation = useSelector((state) => state.myMembership?.customerInformation?.data?.customer);

  const getChatVariables = useMemo(() => {
    if (customerInformation && chat && chat?.api?.updateSystemVariables && shouldGetChatVariables) {
      return () => {
        chat?.api?.updateSystemVariables({
          enteredFormName: customerInformation.fullName,
          enteredChatId: customerInformation.email,
        });
        chat?.api.updateVariables({
          memberNumber: customerInformation.customerNumber,
        });
      };
    }
    return null;
  }, [customerInformation, chat, shouldGetChatVariables]);

  const initChat = useCallback(async () => {
    const { EUWA } = await import('@puzzel/euwa-wrapper');
    if (euwa && EUWA) {
      const returnedChat = await euwa.getApplication(EUWA.APPS.CHAT);
      if (returnedChat) {
        returnedChat?.subscribe('chatWindowShown', () => {
          setShouldGetChatVariables(true);
        });
        setChat(returnedChat);
      }
    }
  }, [euwa]);

  useEffect(() => {
    if (isEnabled && !chat) {
      initChat().then();
    }
  }, [chat, initChat, isEnabled]);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  useEffect(() => {
    if (isEnabled) {
      setInjectedPuzzelChatVisibility(true);
      return;
    }

    chatRef.current?.api?.hide?.();
    chatRef.current?.api?.close?.();
    setInjectedPuzzelChatVisibility(false);
  }, [isEnabled]);

  useEffect(
    () => () => {
      chatRef.current?.api?.hide?.();
      chatRef.current?.api?.close?.();
      setInjectedPuzzelChatVisibility(false);
    },
    [],
  );

  // Update system variables on request
  useEffect(() => {
    if (getChatVariables) {
      getChatVariables();
      setShouldGetChatVariables(false);
    }
  }, [getChatVariables, setShouldGetChatVariables]);
};
